import type { InventoryMembership, SessionUser } from "@iltocat/shared";
import { switchInventory } from "../lib/activeInventory.js";

/**
 * What a switcher calls an inventory. The one made at signup has no name and
 * is "Mine"; every one made since (Stage 7) is named, because two "Mine"s is
 * not a choice. Somebody else's unnamed inventory is still known by whose it is.
 */
export function inventoryLabel(membership: InventoryMembership): string {
  if (membership.name) {
    return membership.role === "owner" ? membership.name : `${membership.name} (${membership.ownerEmail})`;
  }
  return membership.role === "owner" ? "Mine" : membership.ownerEmail;
}

/**
 * Which inventory the app is showing. Lives in the sidebar (and the phone's
 * menu) rather than as a panel on the home screen, since it changes what every
 * screen shows, not just one. Renders nothing when there is only one to choose
 * from, which is every account until somebody shares or makes a second.
 */
export function InventorySwitcher({ user }: { user: SessionUser }) {
  // Archived inventories stay out of the switcher; they open from the
  // Inventories panel. The one being viewed stays listed even if archived,
  // or the select would claim to be showing something it is not.
  const shown = user.memberships.filter(
    (membership) => membership.archivedAt === null || membership.id === user.activeInventoryId,
  );
  if (shown.length < 2) return null;

  return (
    <label className="switcher">
      <span className="switcher-label">Inventory</span>
      <select
        className="input input-small"
        value={user.activeInventoryId}
        onChange={(event) => switchInventory(event.target.value)}
      >
        {shown.map((membership) => (
          <option key={membership.id} value={membership.id}>
            {inventoryLabel(membership)}
            {membership.archivedAt === null ? "" : " (archived)"}
          </option>
        ))}
      </select>
    </label>
  );
}
